import { createAsyncTypes } from 'store/utils';

// State Key
export const KEY = 'clientConfig';

// Action Types
export const types = {
  GET_CONFIG_ASYNC: createAsyncTypes(`${KEY}/GET_CONFIG_ASYNC`)
};

type SupportedLocales =
  | 'deDE'
  | 'enGB'
  | 'enUS'
  | 'esES'
  | 'esMX'
  | 'frCA'
  | 'frFR'
  | 'hiIN'
  | 'itIT'
  | 'jaJP'
  | 'koKR'
  | 'plPL'
  | 'ptBR'
  | 'ptPT'
  | 'viVN'
  | 'zhCN';

export type LocalizedStrRecord = Partial<Record<SupportedLocales, string>>;
export type LocalizedStrListRecord = Partial<Record<SupportedLocales, string[]>>;

export interface Messages {
  readonly header?: LocalizedStrRecord;
  readonly subheader?: LocalizedStrRecord;
  readonly buttons?: LocalizedStrListRecord;
  readonly placeholder?: LocalizedStrRecord;
  /** OLD naming convention, replacing with 'subheader' */
  readonly message?: LocalizedStrRecord;
}

/** OLD way of the localized JSON, replacing with 'Messages' */
export interface Message {
  header?: string;
  message?: string;
  buttons?: string[];
  footer?: string;
  placeholder?: string;
}

export interface CartRule {
  readonly dependentIds: string[];
  readonly reliesOnIds: string[];
}

export interface CartDependencies {
  readonly event?: string[];
  readonly genre?: string[];
  readonly package?: string[];
  readonly donation?: boolean;
}

export interface EventDisplaySettings {
  readonly name?: string;
  readonly showDate?: boolean;
  readonly showTime?: boolean;
  readonly showLocation?: boolean;
  readonly showTier?: boolean;
  readonly showCartItemsPrice?: boolean;
  readonly autoSelectTier?: boolean;
}

export interface ConfigInputField {
  apiParam: string;
  inputType?: string;
  label?: LocalizedStrRecord;
  required?: boolean;
  keyboard?: string;
  halfWidth?: boolean;
  minChar?: number;
  maxChar?: number;
  inputMask?: string;
  placeholder?: LocalizedStrRecord;
}

export interface SurveyField extends ConfigInputField {
  surveyApiParam?: string;
}

export interface SearchOption {
  readonly enabled?: boolean;
  readonly name?: string;
  readonly label?: LocalizedStrRecord;
  readonly lookupOption?: {
    readonly fields?: ConfigInputField[];
    readonly messages?: Messages;
  };
}

export interface WillCallCustomSearch {
  readonly enabled?: boolean;
  readonly tileOptions?: SearchOption[];
}

export interface ScanMembershipCard {
  readonly enabled?: boolean;
  readonly label?: LocalizedStrRecord;
  readonly messages?: Messages;
}

export interface MembershipLookup {
  readonly enabled?: boolean;
  readonly messages?: Messages;
  readonly scanMembershipCard?: ScanMembershipCard;
  readonly customSearchOptions?: SearchOption[];
  readonly tileOrder?: string[];
  readonly notFoundMessages?: Messages;
  readonly loadingMessages?: Messages;
}

export interface ResultCriteria {
  readonly cities?: string[];
  readonly counties?: string[];
  readonly states?: string[];
  readonly countries?: string[];
}

export interface LocationValidation {
  readonly enabled?: boolean;
  readonly overlayMessages?: Messages;
  readonly dataCollection?: {
    readonly keyboard?: string;
    readonly messages?: Messages;
  };
  readonly errorOverlayMessages?: Messages;
  readonly resultCriteria?: ResultCriteria;
}

export interface SouvenirTicket {
  readonly enabled?: boolean;
  readonly ticketEntry?: {
    readonly messages?: Messages;
    readonly productSettingsList?: {
      readonly name?: string;
      readonly upsells?: {
        readonly label?: string;
        readonly price?: number;
      }[];
    }[];
    readonly manualEntry?: {
      readonly enabled?: boolean;
      readonly messages?: Messages;
    };
  };
}

export interface MembershipSalesSettings {
  readonly name: string;
  readonly benefits?: {
    readonly [key: string]: LocalizedStrRecord;
  };
  readonly levelSelection?: {
    readonly messages?: Messages;
  };
  readonly dataCollection?: {
    readonly fields?: ConfigInputField[];
    readonly messages?: Messages;
    readonly confirmationOverlay?: {
      readonly enabled?: boolean;
      readonly messages?: Messages;
    };
  };
  readonly reservation?: {
    readonly loadingMessages?: Messages;
    readonly errorMessages?: Messages;
    readonly renewalOverlayMessages?: Messages;
    readonly upgradeOverlayMessages?: Messages;
    readonly productsToRemoveFromCart?: string[];
  };
}

export interface SurveyItem {
  readonly surveyFields?: SurveyField[];
  readonly subSurveyFields?: SurveyField[];
  readonly cartDependencies?: CartDependencies;
  readonly messages?: Messages;
}

export interface TicketDeliverySettings {
  readonly deliverySelection?: {
    /** Expected values: 'print' | 'email' | 'textMessage' */
    readonly tileOrder?: string[];
    readonly messages?: Messages;
  };
  readonly email?: {
    readonly enabled?: boolean;
    readonly tileText?: LocalizedStrRecord;
    readonly dataCollectionMessages?: Messages;
    readonly deliveryInProgressMessages?: Messages;
    readonly errorOverlayMessages?: Messages;
  };
  readonly print?: {
    readonly enabled?: boolean;
    readonly tileText?: LocalizedStrRecord;
    readonly deliveryInProgress?: {
      readonly ticketsMessages?: Messages;
      readonly receiptsMessages?: Messages;
    };
    readonly errorOverlayMessages?: Messages;
    readonly receiptPrompt?: {
      readonly enabled?: boolean;
      readonly inactivityTimeoutDuration?: number;
      readonly messages?: Messages;
    };
  };
  readonly textMessage?: {
    readonly enabled?: boolean;
    readonly tileText?: LocalizedStrRecord;
    readonly dataCollectionMessages?: Messages;
    readonly deliveryInProgressMessages?: Messages;
    readonly errorOverlayMessages?: Messages;
  };
}

// configs used in normal and citypass ticketing flow
export interface BaseTicketing {
  readonly priceTypeLimit?: number;
  readonly orderLimit?: number;
  readonly supportName?: LocalizedStrRecord;
  readonly filterByName?: boolean;
  readonly filterByDate?: boolean;
  readonly genreSelection?: {
    readonly enabled?: boolean;
    readonly priorityGenres?: string[];
    readonly messages?: Messages;
  };
  readonly autoSelectEvent?: boolean;
  readonly todayEventFlag?: boolean;
  readonly allowPaymentCancelation?: boolean;
  readonly cartRules?: CartRule[];
  readonly customerIdentification?: {
    readonly lookup?: {
      readonly enabled?: boolean;
      readonly lookupList?: {
        readonly fields?: ConfigInputField[];
        readonly subFields?: SurveyField[];
        readonly messages?: Messages;
        readonly cartDependencies?: CartDependencies;
      }[];
      readonly notFound?: {
        readonly enabled?: boolean;
        readonly messages?: Messages;
      };
    };
    readonly creation?: {
      readonly enabled?: boolean;
      readonly creationList?: {
        readonly fields?: ConfigInputField[];
        readonly subFields?: SurveyField[];
        readonly messages?: Messages;
        readonly cartDependencies?: CartDependencies;
      }[];
    };
  };
  readonly ticketDeliverySettings?: TicketDeliverySettings;
  readonly eventTierAvailability?: {
    readonly lowAvailabilityThreshold?: number;
    readonly soldOutThreshold?: number;
    readonly showSoldOut?: boolean;
    readonly messages?: {
      readonly normal?: LocalizedStrRecord;
      readonly lowAvailability?: LocalizedStrRecord;
      readonly soldOut?: LocalizedStrRecord;
      readonly unlimited?: LocalizedStrRecord;
    };
  };
  readonly survey?: {
    readonly enabled?: boolean;
    readonly surveyList?: SurveyItem[];
  };
  readonly eventDisplaySettingsList?: EventDisplaySettings[];
  readonly productAvailabilityAndSelection?: {
    readonly autoLaunchProduct?: string[];
    readonly productsToExcludeFromProductSelection?: string[];
    readonly upsell?: {
      readonly productSelectionMessages?: Messages;
      readonly tierSelectionMessages?: Messages;
      readonly ticketSelectionMessages?: Messages;
      readonly membershipLevelSelectionMessages?: Messages;
    };
    readonly upsellPrompt?: {
      readonly enabled?: boolean;
      readonly productToUpsell?: string;
      readonly productsToInitiateUpsell?: string[];
      readonly numberOfItemsToInitiateUpsell?: number;
      readonly messages?: Messages;
    };
  };
  readonly donations?: {
    readonly amounts?: number[];
    readonly showInProductSelection?: boolean;
    readonly productName?: LocalizedStrRecord;
    readonly messages?: Messages;
    readonly giftAid?: {
      readonly enabled?: boolean;
      readonly messages?: {
        readonly optIn?: LocalizedStrRecord;
        readonly details?: LocalizedStrRecord;
      };
    };
    readonly overlay?: {
      readonly enabled?: boolean;
      readonly roundup?: {
        readonly enabled?: boolean;
        readonly amount?: number;
        readonly message?: LocalizedStrRecord;
      };
      readonly messages?: {
        readonly header?: LocalizedStrRecord;
        readonly subheader?: LocalizedStrRecord;
        readonly buttons?: LocalizedStrListRecord;
      };
    };
  };
  readonly cart?: {
    readonly enabled?: boolean;
    readonly messages?: Messages;
    readonly exitOverlayMessages?: Messages;
    readonly addMoreTickets?: boolean;
    readonly removeTickets?: {
      readonly enabled?: boolean;
      readonly message?: LocalizedStrRecord;
    };
    readonly seatDetailsOverlayMessage?: LocalizedStrRecord;
    readonly expandItems?: boolean;
    readonly expirationOverlayMessages?: Messages;
    readonly giftVoucher?: {
      readonly enabled?: boolean;
      readonly messages?: Messages;
    };
    readonly summary?: {
      readonly enabled?: boolean;
      readonly messages?: Messages;
    };
  };
  readonly priorityPhoneCountryCodes?: string[];
  readonly priorityPostalCodeLookupCountries?: string[];
  readonly souvenirTicket?: SouvenirTicket;
}

export interface PromoCodeCustomOption {
  readonly code?: string;
  readonly messages?: LocalizedStrRecord;
  readonly infoOverlay?: {
    readonly enabled?: boolean;
    readonly messages?: Messages;
  };
  readonly requiresAttendantApproval?: boolean;
}

export interface PromoCodeSelection {
  readonly enabled?: boolean;
  readonly messages?: Messages;
  readonly manualEntry?: {
    readonly enabled?: boolean;
    readonly messages?: LocalizedStrRecord;
  };
  readonly customOptions?: PromoCodeCustomOption[];
  readonly tileOrder?: string[];
  readonly attendantApprovalMessages?: Messages;
}

export interface Ticketing extends BaseTicketing {
  readonly membershipLookup?: MembershipLookup;
  readonly membershipSalesSettingsList?: MembershipSalesSettings[];
  readonly showPriceRange?: boolean;
  readonly promoCode?: {
    readonly priceType?: {
      readonly enabled?: boolean;
      readonly messages?: Messages;
    };
    readonly cart?: {
      readonly enabled?: boolean;
      readonly messages?: Messages;
    };
    readonly selection?: PromoCodeSelection;
  };
  readonly locationValidation?: LocationValidation;
}

export interface Messaging {
  readonly reservingTicket?: Message;
  readonly ticketsUnavailable?: Message;
  readonly eventAPIDown?: Message;
  readonly purchaseError?: Message;
  readonly globalError?: Message;
  readonly ticketServiceDown?: Message;
  readonly checkoutCompletion?: Message;
  readonly ticketSelectionPage?: Message;
  readonly tierPage?: Message;
  readonly purchasePaymentPage?: Message;
  readonly emailSuggestions?: Message;
  readonly eventsPage?: Message;
  readonly orderListPage?: Message;
  readonly packageTierPage?: Message;
  readonly taxesAndFeesLabel?: Message;
}

export interface InfoContent {
  header: string;
  subHeader?: string;
  description?: string;
  image?: string;
}

export interface InfoView {
  label: string;
  content: InfoContent[];
}

export interface LanguageField {
  displayText: string;
  langCode: SupportedLocales;
  isDefault?: boolean;
}

export interface GlobalSettings {
  readonly fonts?: {
    readonly primary: string;
    readonly secondary: string;
  };
  readonly palette?: PaletteConfig;
  readonly attractLoop?: {
    readonly name?: LocalizedStrRecord;
    readonly type: string;
    readonly order: string[];
    readonly transitionTime?: number;
    readonly linkedContentUrl?: string;
    readonly messages?: Messages;
    readonly languageTransitionTime?: number;
    readonly fontColor?: string;
  };
  readonly splashPage?: {
    readonly enabled?: boolean;
    readonly messages?: Messages;
  };
  readonly touchForward?: {
    readonly purchaseFlow?: {
      readonly enabled?: boolean;
    };
    readonly general?: {
      readonly enabled?: boolean;
    };
    readonly ticketDelivery?: {
      readonly enabled?: boolean;
    };
  };
  readonly inactivityTimeout?: {
    readonly duration?: number;
    readonly navigationPrompt?: {
      readonly enabled?: boolean;
      readonly duration?: number;
      readonly buttonZoomSize?: number;
      readonly buttonZoomSpeed?: number;
    };
    readonly abandonSession?: {
      readonly enabled?: boolean;
      readonly duration?: number;
      readonly messages?: Messages;
    };
  };
}

export interface Configuration {
  readonly id: string;
  readonly name: string;
  readonly ticketing: Ticketing;
  readonly willCall: {
    readonly scanMembershipCard?: ScanMembershipCard;
    readonly customSearch?: WillCallCustomSearch;
    readonly willCallTileOrder?: string[];
    readonly skipTicketList?: boolean;
    readonly expandedOrderItems?: boolean;
    readonly selectAllTickets?: boolean;
    readonly errorMessages?: Messages;
    readonly messages?: Messages;
  };
  readonly cityPass: {
    readonly ticketing?: BaseTicketing;
    readonly ticketEntry?: {
      readonly passLimit?: number;
      readonly scanning?: {
        readonly messages?: Messages;
      };
      readonly manualEntry?: {
        readonly enabled?: boolean;
        readonly messages?: Messages;
      };
    };
    readonly autoSelectTickets?: boolean;
  };
  readonly messaging: Partial<Record<SupportedLocales, Messaging>>;
  readonly infoView: undefined | Partial<Record<SupportedLocales, InfoView>>;
  readonly configuration: {
    readonly maintenancePage?: {
      readonly enableLastOrderReprint?: boolean;
    };
    readonly maintenanceLoginTimeout?: number;
    readonly maintenanceTimeout?: number;
    readonly maintenanceTimerCount?: number;
    readonly hideStepper?: boolean;
    readonly languageSettings?: LanguageField[];
    readonly flowTemplate?: undefined | string;
    readonly navigationSelection?: {
      readonly ticketing?: NavigationOption;
      readonly willCall?: NavigationOption;
      readonly info?: NavigationOption;
      readonly cityPass?: NavigationOption;
      readonly donations?: NavigationOption;
      readonly tileOrder?: string[];
      readonly messages?: Messages;
    };
    readonly globalSettings?: GlobalSettings;
    readonly inactivityTimeout?: {
      readonly duration?: number;
      readonly navigationPrompt?: {
        readonly enabled?: boolean;
        readonly duration?: number;
        readonly buttonZoomSize?: number;
        readonly buttonZoomSpeed?: number;
      };
      readonly abandonSession?: {
        readonly enabled?: boolean;
        readonly duration?: number;
        readonly messages?: Messages;
      };
    };
    readonly touchForward?: {
      readonly purchaseFlow?: {
        readonly enabled?: boolean;
      };
      readonly general?: {
        readonly enabled?: boolean;
      };
      readonly ticketDelivery?: {
        readonly enabled?: boolean;
      };
    };
    readonly labels?: {
      readonly cartItem?: LocalizedStrRecord;
      readonly discount?: LocalizedStrRecord;
      readonly product?: {
        singular?: LocalizedStrRecord;
        plural?: LocalizedStrRecord;
      };
      readonly cart?: LocalizedStrRecord;
    };
    readonly inactivePage?: {
      readonly languageTransitionTime?: number;
      readonly messages?: Messages;
    };
  };
}

export interface NavigationOption {
  readonly enabled?: boolean;
  readonly messages?: Messages;
}

export interface PaletteConfig {
  readonly darkPrimary: string;
  readonly lightPrimary: string;
  readonly primary: string;
  readonly secondary: string;
  readonly textIcons: string;
  readonly accent: string;
  readonly primaryText: string;
  readonly secondaryText: string;
  readonly lightSecondary: string;
  readonly darkSecondary: string;
  readonly divider: string;
}

export interface ConfigurationState {
  readonly configuration: Configuration | null;
  readonly loading: boolean;
  readonly error: undefined | string;
}

export interface GetConfigAsyncRequest {
  type: typeof types.GET_CONFIG_ASYNC.REQUEST;
  payload: {
    configurationId: string;
  };
}

export interface GetConfigAsyncError {
  type: typeof types.GET_CONFIG_ASYNC.ERROR;
  payload: string;
}

export interface GetConfigAsyncPending {
  type: typeof types.GET_CONFIG_ASYNC.PENDING;
  payload: boolean;
}

export interface GetConfigAsyncSuccess {
  type: typeof types.GET_CONFIG_ASYNC.SUCCESS;
  payload: Configuration;
}

export type ConfigActionTypes =
  | GetConfigAsyncRequest
  | GetConfigAsyncError
  | GetConfigAsyncPending
  | GetConfigAsyncSuccess;
